import useCreateWorkflow from "api/api-hooks/workflows/useCreateWorkflow";
import useUpdateWorkflow from "api/api-hooks/workflows/useUpdateWorkflow";
import useWorkflow from "api/api-hooks/workflows/useWorkflow";
import { tenantIDAtom } from "atoms/GlobalAtoms";
import AppEmptyState from "elements/AppEmptyState";
import { useAtomValue } from "jotai";
import { useState, useEffect, useRef } from "react";
import { newToastEvent } from "utils/ToastUtil";
import WorkflowUtils from "utils/WorkflowUtils";
import Workflow from "./Workflow";
import WorkflowActionsDrawer from "./WorkflowActionsDrawer/WorkflowActionsDrawer";
import WorkflowDrawer from "./WorkflowDrawer";
import { WorkflowErrors } from "./WorkflowElements";
import WorkflowToolbar from "./WorkflowToolbar";
import { useNavigate, useParams } from "react-router-dom";

// Initial workflow state (used for createMode)
const initialWorkflow = {
  Name: "",
  Description: "",
  Trigger: null,
  Actions: [],
};

/**
 * WorkflowEditor - View for editing and creating Workflows.
 */
const WorkflowEditorView = () => {
  const [workflow, setWorkflow] = useState(initialWorkflow);
  const [workflowErrors, setWorkflowErrors] = useState(null);

  const [workflowDrawer, setWorkflowDrawer] = useState(false);
  const [actionsDrawer, setActionsDrawer] = useState(false);
  const [editAction, setEditAction] = useState(null); // The action currently being edited.

  const { workflowID } = useParams();
  const createMode = workflowID.startsWith("new");
  const { workflow: fetchedWorkflow, workflowLoading } = useWorkflow({
    workflowID: workflowID,
    disabled: createMode,
  });

  const { createWorkflow, creatingWorkflow, createError } = useCreateWorkflow();
  const { updateWorkflow, updatingWorkflow, updateError } = useUpdateWorkflow();

  const navigate = useNavigate();
  const workflowRef = useRef();
  const tenantID = useAtomValue(tenantIDAtom);
  workflowRef.current = workflow;

  // Initalize createMode using the
  useEffect(() => {
    if (workflowID.startsWith("new")) {
      const workflow = workflowRef.current;

      if (!workflow.Name || !workflow.Trigger) {
        setWorkflowDrawer(true);
      }
    }
  }, [workflowID]);

  // Sync fetched workflow with workflow (If not in createMode)
  useEffect(() => {
    if (createMode || workflowLoading) return;

    const wFlow = JSON.parse(JSON.stringify(fetchedWorkflow));

    setWorkflow(wFlow);
    setWorkflowErrors(WorkflowUtils.diagnoseWorkflow(wFlow?.Actions));
  }, [createMode, workflowLoading, fetchedWorkflow]);

  // Re-open drawer on conflict error
  useEffect(() => {
    if (
      typeof createError === "string" &&
      createError.endsWith("already exists.")
    ) {
      setWorkflowDrawer(true);
    }

    if (
      typeof updateError === "string" &&
      updateError.endsWith("already exists.")
    ) {
      setWorkflowDrawer(true);
    }
  }, [createError, updateError]);

  if (!createMode && workflowLoading) {
    return (
      <div className="p-4">
        <div
          className="skeleton rounded"
          style={{ height: 400, width: "100%" }}
        ></div>
      </div>
    );
  }

  const changesMade =
    JSON.stringify(fetchedWorkflow) !== JSON.stringify(workflow);

  return (
    <>
      {/* <Prompt
        when={changesMade}
        message="Unsaved Changes. Are you sure you want to leave?"
      /> */}
      {createMode && (!workflow?.Name || !workflow?.Trigger) && (
        <div className="py-4 px-4">
          <AppEmptyState
            title="Your Workflow"
            description="To begin configuring your workflow, first give it a name and an initial trigger."
            buttonTitle="Open Editor"
            onClick={() => setWorkflowDrawer(true)}
          />
        </div>
      )}
      {workflow?.Name && workflow?.Trigger && (
        <div className="position-relative">
          <WorkflowToolbar
            workflow={workflow}
            openWorkflowDrawer={() => setWorkflowDrawer(true)}
            onSave={saveWorkflow}
            saving={creatingWorkflow || updatingWorkflow}
            changesMade={changesMade}
            cancelChanges={cancelChanges}
            createMode={createMode}
          />
          <Workflow
            workflow={workflow}
            workflowErrors={workflowErrors}
            setActions={setActions}
            openDrawer={(actionID, actionType) => {
              // Handle nested creation (if-else-case Actions) if variables provided.
              if (actionID && actionType) {
                setActionsDrawer({
                  actionID: actionID,
                  actionType: actionType,
                });
              } else {
                setActionsDrawer(true);
              }
            }}
            setEditAction={setEditAction}
          />
          {workflowErrors && <WorkflowErrors workflowErrors={workflowErrors} />}
        </div>
      )}
      <WorkflowDrawer
        open={workflowDrawer}
        onClose={() => setWorkflowDrawer(false)}
        workflow={workflow}
        setWorkflow={setWorkflow}
        error={createError || updateError}
      />
      <WorkflowActionsDrawer
        open={!!actionsDrawer}
        nestedData={typeof actionsDrawer === "object" ? actionsDrawer : null}
        onClose={handleCloseActionsDrawer}
        workflow={workflow}
        setActions={setActions}
        existingAction={editAction}
        handleDeleteAction={handleDeleteAction}
      />
    </>
  );

  // Handles the setting of workflow actions
  // Having it as its own function allows us to add side effects on action updates
  // Like diagnose the workflow for errors whenever the actions are updated.
  function setActions(newActions) {
    setWorkflow((prev) => ({ ...prev, Actions: newActions }));
    setWorkflowErrors(WorkflowUtils.diagnoseWorkflow(newActions));
  }

  function cancelChanges() {
    const loadedWorkflow = JSON.parse(JSON.stringify(fetchedWorkflow));

    setWorkflow(loadedWorkflow);
    setWorkflowErrors(WorkflowUtils.diagnoseWorkflow(loadedWorkflow?.Actions));
  }

  function saveWorkflow() {
    if (createMode) {
      createWorkflow({
        payload: workflow,
        onSuccess: (id) => {
          newToastEvent("success", "Successfully saved Workflow.");
          navigate(`/clouds/${tenantID}/workflows/edit/${id}`, {
            replace: true,
          });
        },
      });
    } else {
      updateWorkflow({
        workflowID: workflowID,
        payload: workflow,
        onSuccess: () =>
          newToastEvent("success", "Successfully saved Workflow."),
      });
    }
  }

  function handleCloseActionsDrawer() {
    setActionsDrawer(false);
    setEditAction(null);
  }

  function handleDeleteAction(actionID) {
    setActions(
      WorkflowUtils.traverseActionsAndUpdate(workflow.Actions, {
        Type: "Delete",
        id: actionID,
      })
    );
    handleCloseActionsDrawer();
  }
};

export default WorkflowEditorView;
